/**
 * Wholesale order quantities must be multiples of minimum order quantity (MOQ).
 */

export function getOrderStep(minimumOrderQuantity) {
  const step = parseInt(minimumOrderQuantity, 10);
  return Number.isFinite(step) && step > 0 ? step : 1;
}

export function getMoqFromProductInfo(productInfo) {
  return getOrderStep(productInfo?.minimum_order_quantity);
}

export function getMoqFromInventory(inventory, product) {
  const invRaw = inventory?.properties?.product_info?.minimum_order_quantity;
  const invParsed = parseInt(invRaw, 10);
  if (Number.isFinite(invParsed) && invParsed > 0) return invParsed;

  const prodParsed = parseInt(product?.minimum_order_quantity, 10);
  if (Number.isFinite(prodParsed) && prodParsed > 0) return prodParsed;

  return 1;
}

/** Largest multiple of step that is <= max (stock cap). */
export function maxOrderQuantityForStock(step, stock) {
  const moq = getOrderStep(step);
  if (stock == null || !Number.isFinite(stock)) return undefined;
  if (stock < moq) return 0;
  return Math.floor(stock / moq) * moq;
}

/** Snap value to a valid bundle count within min/max. */
export function normalizeOrderQuantity(rawValue, { step, min, max } = {}) {
  const moq = getOrderStep(step);
  const minimum = min ?? moq;

  let value = parseInt(rawValue, 10);
  if (!Number.isFinite(value) || value <= 0) {
    return minimum;
  }

  if (value < minimum) {
    value = minimum;
  }

  const remainder = value % moq;
  if (remainder !== 0) {
    value += moq - remainder;
  }

  if (max != null && Number.isFinite(max)) {
    const maxValid = maxOrderQuantityForStock(moq, max);
    if (maxValid === 0) {
      return minimum;
    }
    if (value > maxValid) {
      value = maxValid;
    }
  }

  return value;
}

export function incrementOrderQuantity(current, step, max) {
  const moq = getOrderStep(step);
  const currentQty = normalizeOrderQuantity(current, { step: moq, min: moq, max });
  const next = currentQty + moq;

  if (max != null && Number.isFinite(max)) {
    const maxValid = maxOrderQuantityForStock(moq, max);
    if (maxValid === 0) return currentQty;
    if (next > maxValid) return maxValid;
  }

  return next;
}

export function decrementOrderQuantity(current, step, min) {
  const moq = getOrderStep(step);
  const minimum = min ?? moq;
  const currentQty = normalizeOrderQuantity(current, { step: moq, min: minimum });
  return Math.max(currentQty - moq, minimum);
}

/** How many MOQ bundles a quantity represents. */
export function getBundleCount(quantity, step) {
  const moq = getOrderStep(step);
  const qty = parseInt(quantity, 10);
  if (!Number.isFinite(qty) || qty < moq) return 0;
  return qty / moq;
}

/** Human label for a preset chip — bundles first when MOQ > 1. */
export function formatPresetOption(quantity, step) {
  const moq = getOrderStep(step);
  const qty = parseInt(quantity, 10);
  const bundles = getBundleCount(qty, moq);

  if (moq === 1) {
    return {
      quantity: qty,
      primary: String(qty),
      secondary: qty === 1 ? '1 piece (min)' : `${qty} pieces`,
      bundles,
    };
  }

  const badge = getPresetBadge(bundles);

  if (bundles === 1) {
    return {
      quantity: qty,
      primary: '1 Bundle',
      secondary: `${moq} pcs`,
      bundles: 1,
      badge,
    };
  }

  return {
    quantity: qty,
    primary: `${bundles} Bundles`,
    secondary: `${qty} pcs`,
    bundles,
    badge,
  };
}

/** Marketing badge for bundle preset cards. */
export function getPresetBadge(bundles) {
  if (bundles === 1) return { label: 'Min. order', variant: 'neutral' };
  if (bundles === 2) return { label: 'Most popular', variant: 'popular' };
  if (bundles === 5) return { label: 'Best value', variant: 'value' };
  if (bundles >= 10) return { label: 'Wholesale pick', variant: 'wholesale' };
  if (bundles === 3) return { label: 'Popular', variant: 'popular' };
  return null;
}

/** Copy + numbers for the bundle explainer UI. */
export function getBundleExplainer(step) {
  const piecesPerBundle = getOrderStep(step);

  if (piecesPerBundle === 1) {
    return {
      piecesPerBundle: 1,
      title: 'Minimum order: 1 piece',
      shortTitle: 'Min. 1 piece',
      equation: '1 unit = 1 piece',
      body: 'You can increase quantity one piece at a time.',
      stepLabel: '1 piece',
    };
  }

  return {
    piecesPerBundle,
    title: `1 bundle = ${piecesPerBundle} pieces`,
    shortTitle: `1 bundle = ${piecesPerBundle} pcs`,
    equation: `1 bundle = ${piecesPerBundle} pieces`,
    body: `Minimum order is 1 bundle (${piecesPerBundle} pieces). Each + or − adds another full bundle.`,
    stepLabel: `1 bundle (${piecesPerBundle} pcs)`,
  };
}

/** Live summary for the current selection. */
export function formatBundleOrderSummary(total, step) {
  const moq = getOrderStep(step);
  const pieces = parseInt(total, 10) || 0;
  const bundles = getBundleCount(pieces, moq);

  if (moq === 1) {
    return {
      headline: `${pieces} piece${pieces === 1 ? '' : 's'}`,
      detail: pieces === 1 ? 'Minimum order' : null,
    };
  }

  return {
    headline: `${bundles} bundle${bundles === 1 ? '' : 's'} · ${pieces} pieces`,
    detail: `${bundles} × ${moq} pieces per bundle`,
  };
}

/**
 * MOQ-aligned quick-select options: 1×, 2×, 3×, 5×, 10× bundles (capped by stock).
 * Always includes min bundle; adds max purchasable bundle when it differs.
 */
export function getQuickQuantityPresets(step, { max, maxOptions = 6 } = {}) {
  const moq = getOrderStep(step);
  const multipliers = [1, 2, 3, 5, 10, 15, 20];
  const candidates = multipliers.map((m) => moq * m);

  if (max != null && Number.isFinite(max)) {
    const maxValid = maxOrderQuantityForStock(moq, max);
    if (maxValid >= moq) {
      candidates.push(maxValid);
    }
  }

  const seen = new Set();
  const valid = [];

  candidates.forEach((qty) => {
    if (qty < moq || qty % moq !== 0 || seen.has(qty)) return;
    if (max != null && qty > max) return;
    seen.add(qty);
    valid.push(qty);
  });

  return valid
    .sort((a, b) => a - b)
    .slice(0, maxOptions)
    .map((qty) => formatPresetOption(qty, moq));
}

/** Optional stock line below the explainer. */
export function getBundleStockHint(step, { max } = {}) {
  const moq = getOrderStep(step);
  if (max == null || !Number.isFinite(max)) return null;

  const maxValid = maxOrderQuantityForStock(moq, max);
  if (maxValid === 0) return 'Not enough stock for even 1 bundle.';
  if (maxValid < max) {
    const maxBundles = maxValid / moq;
    return `Up to ${maxBundles} bundle${maxBundles === 1 ? '' : 's'} (${maxValid} pieces) in stock.`;
  }
  return `${max} pieces (${max / moq} bundles) available.`;
}

export function isValidOrderQuantity(quantity, step, { max } = {}) {
  const moq = getOrderStep(step);
  const qty = parseInt(quantity, 10);
  if (!Number.isFinite(qty) || qty < moq || qty % moq !== 0) return false;
  if (max != null && qty > max) return false;
  return true;
}
